// src/shared/components/layout/Aside.jsx
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Skeleton,
  Typography,
  Tooltip,
  Collapse,
  IconButton,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';

import HomeIcon from '@mui/icons-material/Home';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HistoryIcon from '@mui/icons-material/History';
import PaymentsIcon from '@mui/icons-material/Payments';
import LuggageIcon from '@mui/icons-material/Luggage';
import EventNoteIcon from '@mui/icons-material/EventNote';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { NoteAdd, Folder, AutorenewRounded, IntegrationInstructions } from '@mui/icons-material';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { useAuth } from '@app/providers/AuthProvider';
import useResponsive from '@shared/hooks/useResponsive';
import UserAvatar from './Avatar';

const logoSrc = '/images/logo2.png';

const Aside = ({ mobileOpen, handleDrawerToggle, expanded, onToggleExpand }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const location = useLocation();

  // On mobile, "expanded" is implied by `mobileOpen` (drawer is full-width when open).
  // On desktop, the user explicitly controls expand/collapse via the toggle button.
  const isExpanded = isMobile ? mobileOpen : expanded;
  const drawerWidth = isExpanded ? 248 : 76;

  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', `${drawerWidth}px`);
  }, [drawerWidth]);
  const roles = user?.roles ?? [];
  const hasRole = (name) => roles.some((r) => r?.name === name);

  const sections = [
    {
      key: 'general',
      label: 'Core',
      items: [
        {
          key: 'home',
          label: 'Home',
          icon: <HomeIcon />,
          path: '/user-home',
          exact: true,
          disabled: !user,
          loading: !user,
        },
        {
          key: 'booking',
          label: 'Reservation',
          icon: <LuggageIcon />,
          path: '/booking',
          match: (pathname) => pathname.startsWith('/booking'),
          disabled: !user,
          loading: !user,
        },
        {
          key: 'reservations',
          label: 'My Reservations',
          icon: <EventNoteIcon />,
          path: '/reservations',
          match: (pathname) => pathname.startsWith('/reservations'),
          disabled: !user,
          loading: !user,
        },
      ],
    },
  ];

  if (hasRole('MSI Staff')) {
    sections.push({
      key: 'msi-staff',
      label: 'Operations',
      items: [
        {
          key: 'transactions',
          label: 'Transactions',
          icon: <AssignmentIcon />,
          path: '/msi-transactions',
        },
        {
          key: 'history',
          label: 'History',
          icon: <HistoryIcon />,
          path: '/transactions/log',
          match: (pathname) => pathname.startsWith('/transactions'),
        },
        {
          key: 'payments',
          label: 'Payments',
          icon: <PaymentsIcon />,
          path: '/vendor-payments',
        },
        {
          key: 'customer-fees',
          label: 'Customer Fees',
          icon: <ReceiptLongIcon />,
          path: '/customer-fees',
          match: (pathname) => pathname.startsWith('/customer-fees'),
        },
      ],
    });
  }

  if (hasRole('Vendor')) {
    sections.push({
      key: 'vendor',
      label: 'Vendor Tools',
      items: [
        {
          key: 'check-in',
          label: 'Employee Check-In',
          icon: <NoteAdd />,
          path: '/check-in',
          match: (pathname) => pathname.startsWith('/check-in'),
        },
        {
          key: 'drafts',
          label: 'Transaction Drafts',
          icon: <Folder />,
          path: '/saved',
        },
        {
          key: 'status',
          label: 'Transaction Status',
          icon: <AutorenewRounded />,
          path: '/folios',
        },
        {
          key: 'booking',
          label: 'Reservation',
          icon: <LuggageIcon />,
          path: '/booking',
        },
        
      ],
    });
  }

  if (
    hasRole('Admin') ||
    hasRole('Super Admin') ||
    hasRole('MSI Staff') ||
    hasRole('Vendor')
  ) {
    sections.push({
      key: 'billing',
      label: 'Billing',
      items: [
        {
          key: 'billing-main',
          label: 'Billing',
          icon: <ReceiptLongIcon />,
          path: '/billing',
          match: (pathname) => pathname === '/billing',
        },
        {
          key: 'billing-batches',
          label: 'Billing Batches',
          icon: <FolderCopyIcon />,
          path: '/billing/batches',
          match: (pathname) =>
            pathname.startsWith('/billing/') &&
            pathname !== '/billing' &&
            pathname !== '/billing/record-payment',
        },
        
        
        
      ],
    });
  }

  if (
    hasRole('Admin') ||
    hasRole('Super Admin') ||
    hasRole('MSI Staff') ||
    hasRole('Vendor')
  ) {
    sections.push({
      key: 'payment',
      label: 'Payment',
      items: [
        {
          key: 'payment',
          label: 'Payment',
          icon: <PaymentsIcon />,
          path: '/billing/record-payment',
          match: (pathname) => pathname === '/billing/record-payment',
        },
      ],
    });
  }

  if (hasRole('Travel Manager') || hasRole('Admin') || hasRole('Super Admin')) {
    sections.push({
      key: 'travel-mgr',
      label: 'People',
      items: [
        {
          key: 'employees',
          label: 'Employees',
          icon: <SupervisedUserCircleIcon />,
          path: '/employees',
          match: (pathname) => pathname.startsWith('/employees'),
        },
        {
          key: 'staff-reservations',
          label: 'Staff Reservations',
          icon: <EventNoteIcon />,
          path: '/staff-reservations',
          match: (pathname) => pathname.startsWith('/staff-reservations'),
        },
      ],
    });
  }

  if (hasRole('Admin') || hasRole('Super Admin')) {
    sections.push({
      key: 'admin',
      label: 'Admin',
      items: [
        {
          key: 'users',
          label: 'User Management',
          icon: <ManageAccountsIcon />,
          path: '/users',
          match: (pathname) => pathname.startsWith('/users'),
        },
        {
          key: 'chains',
          label: 'Hotel Chains',
          icon: <AccountTreeIcon />,
          path: '/chains',
          match: (pathname) => pathname.startsWith('/chains'),
        },
        {
          key: 'vendors',
          label: 'Vendor Management',
          icon: <AddBusinessIcon />,
          path: '/vendors',
          match: (pathname) =>
            pathname === '/vendors' ||
            (pathname.startsWith('/vendors/') &&
              !pathname.startsWith('/vendors/derby-status')),
        },
        {
          key: 'derby-status',
          label: 'Derby Status',
          icon: <ToggleOnIcon />,
          path: '/vendors/derby-status',
          match: (pathname) => pathname.startsWith('/vendors/derby-status'),
        },
        {
          key: 'customer-fees',
          label: 'Customer Fees',
          icon: <ReceiptLongIcon />,
          path: '/customer-fees',
          match: (pathname) => pathname.startsWith('/customer-fees'),
        },
        {
          key: 'payment-exceptions',
          label: 'Payment Exceptions',
          icon: <ReportProblemOutlinedIcon />,
          path: '/payment-exceptions',
          match: (pathname) => pathname.startsWith('/payment-exceptions'),
        },
        {
          key: 'integrations',
          label: 'Integrations',
          icon: <IntegrationInstructions />,
          path: '/integrations',
          match: (pathname) => pathname.startsWith('/integrations'),
        },
        // {
        //   key: 'employees',
        //   label: 'Employee Management',
        //   icon: <SupervisedUserCircleIcon />,
        //   path: '/employees',
        //   match: (pathname) => pathname.startsWith('/employees'),
        // },
        // {
        //   key: 'admin-transactions',
        //   label: 'Transactions',
        //   icon: <AssignmentIcon />,
        //   path: '/msi-transactions',
        // },
        // {
        //   key: 'admin-history',
        //   label: 'History',
        //   icon: <HistoryIcon />,
        //   path: '/transactions/log',
        //   match: (pathname) => pathname.startsWith('/transactions'),
        // },
        // {
        //   key: 'admin-payments',
        //   label: 'Vendor Payments',
        //   icon: <PaymentsIcon />,
        //   path: '/vendor-payments',
        // },
      ],
    });
  }

  const visibleSections = sections.filter((section) => section.items.length);
  const findActiveSectionKey = () => {
    const active = visibleSections.find((section) =>
      section.items.some((item) => {
        if (!item.path) return false;
        if (item.match) return item.match(location.pathname);
        if (item.exact) return location.pathname === item.path;
        return location.pathname.startsWith(item.path);
      })
    );
    return active?.key ?? visibleSections[0]?.key ?? null;
  };
  const [openSections, setOpenSections] = React.useState(() => {
    const initialKey = findActiveSectionKey();
    return initialKey ? [initialKey] : [];
  });
  const prevPathRef = React.useRef(location.pathname);

  React.useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      const nextKey = findActiveSectionKey();
      prevPathRef.current = location.pathname;
      if (nextKey) {
        setOpenSections((prev) => (prev.includes(nextKey) ? prev : [...prev, nextKey]));
      }
    }
  }, [location.pathname, visibleSections]);

  const NavItem = ({ item }) => {
    const {
      icon,
      label,
      path,
      match,
      disabled = false,
      loading = false,
      exact = false,
    } = item;

    const isSelected = match
      ? match(location.pathname)
      : exact
      ? location.pathname === path
      : path
      ? location.pathname.startsWith(path)
      : false;

    const handleNavigate = () => {
      if (disabled || !path) return;
      navigate(path);
      if (isMobile) {
        handleDrawerToggle();
      }
    };

    const button = (
      <ListItemButton
        onClick={handleNavigate}
        disabled={disabled}
        selected={isSelected}
        sx={{
          borderRadius: 2,
          mx: isExpanded ? 1.25 : 1,
          px: isExpanded ? 2 : 1,
          minHeight: 42,
          justifyContent: isExpanded ? 'flex-start' : 'center',
          transition: 'background-color 0.25s ease, padding 0.25s ease',
          color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
          backgroundColor: isSelected
            ? alpha(theme.palette.primary.main, 0.12)
            : 'transparent',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
          '&.Mui-selected:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.16),
          },
          '&.Mui-disabled': {
            opacity: 0.5,
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: isExpanded ? 1.5 : 0,
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: isSelected
                ? alpha(theme.palette.primary.main, 0.16)
                : alpha(theme.palette.primary.main, 0.08),
              boxShadow: isSelected ? '0 6px 14px rgba(15, 23, 42, 0.12)' : 'none',
              transition: 'all 0.2s ease',
              color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
              '& svg': {
                fontSize: 20,
              },
            }}
          >
            {icon}
          </Box>
        </ListItemIcon>
        <ListItemText
          primary={loading ? <Skeleton variant="text" width={80} /> : label}
          sx={{
            opacity: isExpanded ? 1 : 0,
            maxWidth: isExpanded ? 180 : 0,
            transition: 'opacity 0.2s ease, max-width 0.2s ease',
            overflow: 'hidden',
          }}
          slotProps={{ primary: { variant: 'body2', fontWeight: 600, color: 'inherit' } }}
        />
      </ListItemButton>
    );

    if (isExpanded) {
      return button;
    }

    return (
      <Tooltip title={label} placement="right">
        <span>{button}</span>
      </Tooltip>
    );
  };

  const SectionHeader = ({ section }) => {
    if (!isExpanded || !section.label) {
      return null;
    }

    const isOpen = openSections.includes(section.key);

    const handleToggle = () => {
      setOpenSections((prev) =>
        prev.includes(section.key)
          ? prev.filter((key) => key !== section.key)
          : [...prev, section.key]
      );
    };

    return (
      <ListItemButton
        onClick={handleToggle}
        sx={{
          borderRadius: 2,
          mb: 0.5,
          px: 1.5,
          py: 0.75,
          minHeight: 36,
          color: 'text.secondary',
          backgroundColor: isOpen
            ? alpha(theme.palette.primary.main, 0.08)
            : 'transparent',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
          },
          '& .section-toggle': {
            opacity: 0,
            transition: 'opacity 0.2s ease',
          },
          '&:hover .section-toggle': {
            opacity: 1,
          },
        }}
        aria-expanded={isOpen}
      >
        <ListItemText
          primary={section.label}
          slotProps={{ primary: { variant: 'caption', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' } }}
        />
        <Box className="section-toggle" sx={{ display: 'flex', alignItems: 'center' }}>
          {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Box>
      </ListItemButton>
    );
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileOpen : true}
      onClose={handleDrawerToggle}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          border: 'none',
          backgroundColor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.9)
              : alpha(theme.palette.background.paper, 0.96),
          backgroundImage: 'none',
          boxShadow: 'none',
          borderRight: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          position: 'relative',
          height: '100%',
          transition: theme.transitions.create('width', {
            duration: theme.transitions.duration.shorter,
          }),
          // Allow the floating expand/collapse chevron to overhang
          // the right edge of the drawer.
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ px: isExpanded ? 1.5 : 1, position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            justifyContent: isExpanded ? 'flex-start' : 'center',
            p: isExpanded ? 1.5 : 1,
            borderRadius: 2,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.88)
                : alpha(theme.palette.background.paper, 0.98),
          }}
        >
          <Box
            sx={{
              width: isExpanded ? 46 : 36,
              height: isExpanded ? 46 : 36,
              borderRadius: 2.5,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={logoSrc}
              alt="MSI Logo"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                p: isExpanded ? 0.6 : 0.5,
              }}
            />
          </Box>
          {isExpanded && (
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                MSI Lodging
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                Operations Portal
              </Typography>
            </Box>
          )}
        </Box>

        {/* Desktop expand/collapse toggle — sits on the drawer's right edge so
            it's always discoverable without crowding the header content. */}
        {!isMobile && onToggleExpand && (
          <Tooltip title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'} placement="right">
            <IconButton
              onClick={onToggleExpand}
              size="small"
              aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
              sx={{
                position: 'absolute',
                top: '50%',
                right: -14,
                transform: 'translateY(-50%)',
                width: 28,
                height: 28,
                bgcolor: 'background.paper',
                border: `1px solid ${theme.palette.divider}`,
                color: theme.palette.text.secondary,
                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)',
                zIndex: 2,
                transition: 'all 180ms ease',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              {isExpanded
                ? <ChevronLeftIcon fontSize="small" />
                : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Divider sx={{mx: isExpanded ? 2 : 0, opacity: 0.4 }} />
      <List
        component="nav"
        sx={{
          py: 2,
          flexGrow: 1,
          gap: 0.5,
          display: 'flex',
          flexDirection: 'column',
          px: isExpanded ? 1.5 : 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(theme.palette.divider, 0.5),
            borderRadius: 4,
          },
        }}
      >
        {visibleSections.map((section, index) => (
          <Box
            component="div"
            key={section.key}
            sx={{ mt: index === 0 ? 0 : 1.5 }}
          >
            <SectionHeader section={section} />
            {isExpanded ? (
              <Collapse in={openSections.includes(section.key)} timeout="auto" unmountOnExit>
                {section.items.map((item, itemIndex) => (
                  <Box
                    key={item.key}
                    sx={{ mb: itemIndex < section.items.length - 1 ? 0.75 : 0 }}
                  >
                    <NavItem item={item} />
                  </Box>
                ))}
              </Collapse>
            ) : (
              section.items.map((item, itemIndex) => (
                <Box
                  key={item.key}
                  sx={{ mb: itemIndex < section.items.length - 1 ? 0.75 : 0 }}
                >
                  <NavItem item={item} />
                </Box>
              ))
            )}
            {index < visibleSections.length - 1 && (
              <Divider sx={{ mt: isExpanded ? 1.5 : 1, opacity: 0.35 }} />
            )}
          </Box>
        ))}
      </List>

      <Box
        sx={{
          mt: 'auto',
          px: isExpanded ? 1.5 : 1,
          pb: 2,
          pt: 1,
        }}
      >
        <Divider/>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: isExpanded ? 1.25 : 1,
            borderRadius: 2,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.9)
                : alpha(theme.palette.background.paper, 0.98),
          }}
        >
          <UserAvatar
            name={`${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() || 'User'}
            size={isExpanded ? 56 : 42}
          />
          {isExpanded && (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} noWrap>
                {user?.first_name || 'User'} {user?.last_name || ''}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {user?.email || 'user@example.com'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default Aside;